import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { Field } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';

import CommonInfoCard from './CommonInfoCard';
import PlatformInfoCard from './PlatformInfoCard';
import { STAGE_OPTIONS } from './ProjectStageEditor';
import { emptyPlatformState, PLATFORM_KEYS } from './infoTabFields';
import type { CommonState, PlatformState } from './infoTabFields';
import { commonBeforeText, editValueText, projectEditDiff, rawText } from './projectEditDiff';
import type { ProjectEditChange, ProjectEditDiff } from './projectEditDiff';
import styles from './InfoTab.module.css';

// 요청 상세에서 project_create / project_edit 요청의 입력값을, 입력 화면과 동일한
// CommonInfoCard / PlatformInfoCard를 '읽기전용'으로 재활용해 보여준다.
// → 입력 폼이 바뀌면 이 확인 화면도 자동으로 같은 모양으로 반영된다.

type Dict = Record<string, unknown>;

function s(v: unknown): string {
  if (v === null || v === undefined || typeof v === 'object') return '';
  return String(v);
}

function asDict(v: unknown): Dict {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Dict) : {};
}

function normCode(label: string): string {
  return (label || '').toLowerCase();
}

interface Reconstructed {
  showCommon: boolean;
  common: CommonState;
  codes: string[];
  platforms: Record<string, PlatformState>;
  // before 가 있는 수정 요청에서만 — 바뀌는 칸의 이전 값과 Stage 칸.
  commonChangedFrom?: Partial<Record<keyof CommonState, string>>;
  platformChangedFrom?: Record<string, Partial<Record<keyof PlatformState, string>>>;
  stage?: { value: string; changedFrom?: string };
}

// canonical payload: { display_name?, app_match_name, ga_game_id, tapjoy_app_id,
//   gcp_project_id, publisher, company, stores:[{code, store_id, bundle_id?, sku?, ga_game_id?,
//   tapjoy_app_id?, state_code?}] }
//   ※ bundle_id는 플랫폼(store)별 값으로 이동 — 최상위 공통 필드가 아니다.
//   (레거시 payload에는 최상위 bundle_id가 있을 수 있어 store별로 fallback 표시)
// 레거시(이미 요청함에 쌓인) payload: { ..., os:[...], aos:{store_id}, ios:{store_id, sku} }
function fromCreate(payload: Dict): Reconstructed {
  const common: CommonState = {
    display_name: s(payload.display_name),
    app_match_name: s(payload.app_match_name),
    ga_game_id: s(payload.ga_game_id),
    tapjoy_app_id: s(payload.tapjoy_app_id),
    adjust_app_token: s(payload.adjust_app_token),
    gcp_project_id: s(payload.gcp_project_id),
    publisher: s(payload.publisher),
    company: s(payload.company),
  };
  const codes: string[] = [];
  const platforms: Record<string, PlatformState> = {};

  const stores = Array.isArray(payload.stores) ? payload.stores : null;
  if (stores && stores.length > 0) {
    // 신규 canonical stores[] — store dict에서 값을 직접 읽는다(공통값 오표시 없음).
    for (const raw of stores) {
      const st = asDict(raw);
      const code = normCode(s(st.code) || s(st.store));
      if (!code || platforms[code]) continue;
      codes.push(code);
      platforms[code] = {
        ...emptyPlatformState(),
        store_id: s(st.store_id),
        store_url: s(st.store_url),
        meta_app_id: s(st.meta_app_id),
        sku: s(st.sku),
        bundle_id: s(st.bundle_id) || s(payload.bundle_id),
        ga_game_id: s(st.ga_game_id) || s(payload.ga_game_id),
        tapjoy_app_id: s(st.tapjoy_app_id) || s(payload.tapjoy_app_id),
        dt_app_id: s(st.dt_app_id),
        unity_game_id: s(st.unity_game_id),
        vungle_app_id: s(st.vungle_app_id),
        state_code: s(st.state_code) || '1',
      };
    }
  } else {
    // 레거시 {os, aos, ios} payload 호환.
    const codeSet = new Set<string>();
    if (Array.isArray(payload.os)) for (const o of payload.os) codeSet.add(normCode(s(o)));
    for (const c of ['aos', 'ios', 'web', 'steam']) if (payload[c]) codeSet.add(c);
    for (const code of [...codeSet].filter(Boolean)) {
      const p = asDict(payload[code]);
      codes.push(code);
      platforms[code] = {
        ...emptyPlatformState(),
        store_id: s(p.store_id),
        store_url: s(p.store_url),
        meta_app_id: s(p.meta_app_id),
        sku: s(p.sku),
        bundle_id: s(payload.bundle_id),
        ga_game_id: s(p.ga_game_id) || s(payload.ga_game_id),
        tapjoy_app_id: s(p.tapjoy_app_id) || s(payload.tapjoy_app_id),
        dt_app_id: s(p.dt_app_id),
        unity_game_id: s(p.unity_game_id),
        vungle_app_id: s(p.vungle_app_id),
        state_code: s(p.state_code) || '1',
      };
    }
  }
  return { showCommon: true, common, codes, platforms };
}

// project_edit payload(before 없는 옛 요청): { common:{변경필드}, platforms:{code:{변경필드}}, prefix }
// 비교할 이전 값이 없어 요청한 칸만 채워 보인다.
function fromEdit(payload: Dict): Reconstructed {
  const commonChanges = asDict(payload.common);
  const common: CommonState = {
    display_name: s(commonChanges.display_name),
    app_match_name: s(commonChanges.app_match_name),
    ga_game_id: s(commonChanges.ga_game_id),
    tapjoy_app_id: s(commonChanges.tapjoy_app_id),
    adjust_app_token: s(commonChanges.adjust_app_token),
    gcp_project_id: s(commonChanges.gcp_project_id),
    publisher: s(commonChanges.publisher),
    company: s(commonChanges.company),
  };
  const platChanges = asDict(payload.platforms);
  const codes = Object.keys(platChanges);
  const platforms: Record<string, PlatformState> = {};
  for (const code of codes) {
    const c = asDict(platChanges[code]);
    platforms[code] = {
      ...emptyPlatformState(),
      sub_project_id: s(c.sub_project_id),
      store_id: s(c.store_id),
      store_url: s(c.store_url),
      meta_app_id: s(c.meta_app_id),
      bundle_id: s(c.bundle_id),
      sku: s(c.sku),
      ga_game_id: s(c.ga_game_id),
      tapjoy_app_id: s(c.tapjoy_app_id),
      dt_app_id: s(c.dt_app_id),
      unity_game_id: s(c.unity_game_id),
      vungle_app_id: s(c.vungle_app_id),
      state_code: s(c.state_code) || '1',
    };
  }
  return { showCommon: Object.keys(commonChanges).length > 0, common, codes, platforms };
}

const COMMON_KEYS: (keyof CommonState)[] = [
  'display_name',
  'app_match_name',
  'ga_game_id',
  'tapjoy_app_id',
  'adjust_app_token',
  'gcp_project_id',
  'publisher',
  'company',
];

// project_edit payload(before 있음): 접수 시점의 전체 값 위에 요청 값을 얹어 편집 화면과 같은
// 카드로 보이고, 바뀌는 칸에는 이전 값을 붙인다.
function fromEditWithBefore(payload: Dict, diff: ProjectEditDiff): Reconstructed {
  const commonChanges = asDict(payload.common);
  const merged = { ...diff.beforeCommon, ...commonChanges };
  const common = Object.fromEntries(COMMON_KEYS.map((key) => [key, rawText(merged[key])])) as unknown as CommonState;
  const commonChangedFrom: Partial<Record<keyof CommonState, string>> = {};
  for (const key of COMMON_KEYS) {
    if (key in commonChanges) commonChangedFrom[key] = editValueText(key, diff.beforeCommon[key]);
  }
  const stage = {
    value: rawText(merged.stage),
    changedFrom: 'stage' in commonChanges
      ? commonBeforeText('stage', diff.beforeCommon, diff.beforePlatforms)
      : undefined,
  };

  const platChanges = asDict(payload.platforms);
  const codes = [...new Set([...Object.keys(diff.beforePlatforms), ...Object.keys(platChanges)])];
  const platforms: Record<string, PlatformState> = {};
  const platformChangedFrom: Record<string, Partial<Record<keyof PlatformState, string>>> = {};
  for (const code of codes) {
    const previous = diff.beforePlatforms[code] ?? {};
    const changes = asDict(platChanges[code]);
    const values = { ...previous, ...changes };
    platforms[code] = {
      ...emptyPlatformState(),
      ...Object.fromEntries(PLATFORM_KEYS.map((key) => [key, rawText(values[key])])),
    };
    for (const key of PLATFORM_KEYS) {
      if (key in changes) {
        platformChangedFrom[code] = { ...platformChangedFrom[code], [key]: editValueText(key, previous[key]) };
      }
    }
  }
  return { showCommon: true, common, codes, platforms, commonChangedFrom, platformChangedFrom, stage };
}

// 변경 목록 — 편집 화면 '변경 사항' 카드와 같은 줄 모양(InfoTab.module.css)을 쓴다.
function ChangeList({ changes, hasBefore }: { changes: ProjectEditChange[]; hasBefore: boolean }) {
  return (
    <div className={styles.card} style={{ padding: 0, marginTop: 10 }}>
      <div className={styles.dirtyHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className={styles.sectionInline}>변경 사항</div>
          <span className={styles.diffBadge}>{changes.length}개 항목</span>
        </div>
        <span className={styles.diffEmpty}>
          {hasBefore ? '이전 값은 요청을 보낸 시점의 값이에요' : '이전 값 기록 없음'}
        </span>
      </div>
      <div className={styles.diffBody}>
        {!hasBefore && (
          <div className={styles.sectionSub} style={{ marginTop: 0, marginBottom: 12 }}>
            이 요청은 이전 값을 함께 저장하기 전에 접수됐어요. 요청한 새 값만 보여 드려요.
          </div>
        )}
        <div className={styles.diffList} style={{ marginBottom: 0 }}>
          {changes.map((d) => (
            <div key={`${d.scope}-${d.field}`} className={styles.diffRow}>
              <span className={styles.diffScope}>{d.scope}</span>
              <span className={styles.diffField}>{d.field}</span>
              <span className={styles.diffBefore} style={hasBefore ? undefined : { textDecoration: 'none' }}>
                {d.before}
              </span>
              <span className={styles.diffAfter}>
                <NavIcon name="chev-r" color="#9B9B9B" size={10} />
                {d.after}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// 요청 상세 카드 보기 전환 — 전체 값 / 이번에 바뀌는 칸만.
function ViewToggle({ onlyChanged, onChange }: { onlyChanged: boolean; onChange: (next: boolean) => void }) {
  const item = (active: boolean): CSSProperties => ({
    font: '600 12px/1 "Pretendard"',
    padding: '7px 12px',
    borderRadius: 999,
    border: 0,
    cursor: 'pointer',
    background: active ? 'var(--sc-ink-0)' : 'transparent',
    color: active ? '#fff' : 'var(--sc-ink-7)',
  });
  return (
    <div role="group" aria-label="카드 보기" style={{ display: 'inline-flex', border: '1px solid var(--ui-input-border)', borderRadius: 999, padding: 2, background: '#fff' }}>
      <button type="button" aria-pressed={!onlyChanged} style={item(!onlyChanged)} onClick={() => onChange(false)}>
        전체 보기
      </button>
      <button type="button" aria-pressed={onlyChanged} style={item(onlyChanged)} onClick={() => onChange(true)}>
        바뀐 칸만
      </button>
    </div>
  );
}

interface ProjectRequestCardsProps {
  // 'project_create' | 'project_edit'
  requestType: string;
  payload: Dict;
}

export default function ProjectRequestCards({ requestType, payload }: ProjectRequestCardsProps) {
  const isCreate = requestType === 'project_create';
  const projectId = s(payload.project_id);
  const diff = useMemo(() => (isCreate ? null : projectEditDiff(payload)), [isCreate, payload]);
  const { showCommon, common, codes, platforms, commonChangedFrom, platformChangedFrom, stage } = useMemo(
    () => (isCreate ? fromCreate(payload) : diff?.hasBefore ? fromEditWithBefore(payload, diff) : fromEdit(payload)),
    [isCreate, payload, diff],
  );
  const [onlyChanged, setOnlyChanged] = useState(false);
  const changedCodes = codes.filter((code) => Object.keys(platformChangedFrom?.[code] ?? {}).length > 0);
  const visibleCodes = onlyChanged ? changedCodes : codes;
  // 처음 열 때는 바뀐 칸이 있는 OS 탭부터 보인다.
  const [activeCode, setActiveCode] = useState<string>(changedCodes[0] ?? codes[0] ?? '');
  const active = visibleCodes.includes(activeCode) ? activeCode : (visibleCodes[0] ?? '');
  const commonVisible = showCommon
    && (!onlyChanged || Object.keys(commonChangedFrom ?? {}).length > 0 || stage?.changedFrom !== undefined);

  return (
    <div>
      {!isCreate && /^\d{5}$/.test(projectId) && (
        <div className={styles.sectionSub}>
          수정 대상: <Link to={`/projects/${projectId}`}>Project ID {projectId}</Link>
        </div>
      )}
      {diff && diff.changes.length > 0 && <ChangeList changes={diff.changes} hasBefore={diff.hasBefore} />}
      {!isCreate && (diff?.hasBefore ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '4px 0 10px' }}>
          <div style={{ font: '700 13px/1.4 "Pretendard"' }}>요청한 정보 전체</div>
          <div style={{ font: '500 12px/1.5 "Pretendard"', color: 'var(--sc-text-muted)' }}>
            편집 화면과 같은 카드예요. 보라색 칸이 이번에 바뀌는 값입니다.
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <ViewToggle onlyChanged={onlyChanged} onChange={setOnlyChanged} />
          </div>
        </div>
      ) : (
        <div style={{ font: '500 12px/1.5 "Pretendard"', color: 'var(--sc-text-muted)', marginBottom: 10 }}>
          변경 요청된 항목만 표시됩니다.
        </div>
      ))}
      {commonVisible && (
        <CommonInfoCard
          projectId={projectId}
          common={common}
          ver={0}
          onChangeField={() => {}}
          readonly
          changedFrom={commonChangedFrom}
          onlyChanged={onlyChanged}
          stageEditor={stage && (!onlyChanged || stage.changedFrom !== undefined) && (
            <Field
              label="Stage"
              readonly
              selectOptions={STAGE_OPTIONS}
              value={stage.value}
              changedFrom={stage.changedFrom}
              help="모든 OS의 PSL·분석 Stage와 Stage별 조회 권한에 적용됩니다."
            />
          )}
        />
      )}
      {visibleCodes.length > 0 && (
        <PlatformInfoCard
          allPlatformCodes={visibleCodes}
          activeCode={active}
          onSelectCode={setActiveCode}
          platforms={platforms}
          addingCodes={[]}
          isAdmin={false}
          ver={0}
          readonly
          showOverlay={false}
          hasActiveInitial
          onPickAdd={() => {}}
          onChangeField={() => {}}
          changedFrom={platformChangedFrom}
          onlyChanged={onlyChanged}
        />
      )}
      {!showCommon && codes.length === 0 && (
        <div style={{ color: 'var(--sc-text-muted)' }}>표시할 입력 내용이 없어요</div>
      )}
    </div>
  );
}
